import { useMemo } from 'react';
import { FileText } from 'lucide-react';
import { NAV_ITEMS, isGroup, type NavItem, type NavLeaf } from '@/components/layout/nav-config';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { useDocuments, useSheets } from './api';

const GROUP_LABEL = 'API Docs';

/**
 * Same as old_obd: reseller (ADMIN) and user see an "API Docs" group that lists
 * every sheet and every document shared with them. Super / sub-super admins keep
 * the static management children from NAV_ITEMS.
 */
export function useApiDocsNavItems(): NavItem[] {
  const role = useAuthStore((s) => s.role);
  const isViewer = role === USER_ROLES.USER || role === USER_ROLES.ADMIN;
  const sheets = useSheets(isViewer);
  const documents = useDocuments(isViewer);

  return useMemo<NavItem[]>(() => {
    if (!isViewer) return NAV_ITEMS;
    const viewerRoles = [USER_ROLES.USER, USER_ROLES.ADMIN];
    const leaves: NavLeaf[] = [
      ...(sheets.data ?? []).map((s) => ({
        label: s.sheetName,
        to: `/api-docs/${s.id}`,
        icon: FileText,
        roles: viewerRoles,
      })),
      ...(documents.data ?? []).map((d) => ({
        label: d.documentName,
        to: `/api-docs/document/${d.id}`,
        icon: FileText,
        roles: viewerRoles,
      })),
    ];
    if (leaves.length === 0) {
      leaves.push({ label: 'No documents yet', to: '/api-docs', roles: viewerRoles });
    }
    return NAV_ITEMS.map((item) =>
      isGroup(item) && item.label === GROUP_LABEL ? { ...item, children: leaves } : item,
    );
  }, [isViewer, sheets.data, documents.data]);
}
